import * as React from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Copyright from '../Copyright';
import CustomizedDataGrid from './CustomizedDataGrid';
import StatCard from './StatCard';
import { canViewTransactions } from '../../helpers/authSession';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { getFilterConfig } from '../../helpers/filterLabelGenerator';
import { rulesEngineGet } from '../../services/rulesEngineClient';
import { getRulesEngineUiMessage } from '../../services/rulesEngineErrors';
import { getRulesEngineAnalyticsUrl } from '../../services/rulesEngineEndpoints';
import {
  AML_TRANSACTIONS_GRID_TEXT,
  AML_TRANSACTIONS_FINANCIAL_CARDS,
  AML_TRANSACTIONS_QUANTITY_CARDS,
  ANALYSIS_STATUS_BY_TRANSACTION_CARD_TITLE,
  formatBrl,
  formatBrlTooltipValue,
  formatCount,
  formatPercentage,
  formatHistoryLabel,
  generateBucketKeys,
  getApiDateRangeFromFilterState,
  getApiGroupByFromFilterState,
  getIntervalLabelByFilter,
  getTrendByPercentage,
  normalizeBucketKey,
} from '../../helpers/transactionMetrics';

const STATUS_TONE_BY_CARD_TITLE = {
  Total: 'total',
  'Aguardando Análise': 'waiting',
  'Em Análise': 'analysis',
  Reportadas: 'report',
  Finalizadas: 'finalized',
};

const ANALYSIS_STATUS_TABS = [
  { value: 'ALL', label: 'Todos' },
  { value: 'WAITING_ANALYSIS', label: 'Aguardando Análise' },
  { value: 'ANALYSIS', label: 'Em Análise' },
  { value: 'REPORT', label: 'Reportadas' },
  { value: 'FINALIZED', label: 'Finalizadas' },
];

const VALID_ANALYSIS_STATUS_VALUES = new Set(ANALYSIS_STATUS_TABS.map((tab) => tab.value));

function normalizeAnalysisStatus(value) {
  if (!value)
    return 'ALL';

  return VALID_ANALYSIS_STATUS_VALUES.has(value) ? value : 'ALL';
}


export default function TransactionMainGrid() {
  const canView = canViewTransactions();
  const [searchParams, setSearchParams] = useSearchParams();
  const analysisStatus = React.useMemo(() => normalizeAnalysisStatus(searchParams.get('status')), [searchParams]);
  const filter = useSelector((state) => state.filter.value);
  const config = getFilterConfig(filter);
  const intervalLabel = React.useMemo(() => getIntervalLabelByFilter(filter), [filter]);
  const [overviewData, setOverviewData] = React.useState(null);
  const [analysisStatusHistory, setAnalysisStatusHistory] = React.useState([]);
  const [isLoadingOverview, setIsLoadingOverview] = React.useState(true);
  const [isLoadingAnalysisStatusHistory, setIsLoadingAnalysisStatusHistory] = React.useState(true);
  const [overviewErrorMessage, setOverviewErrorMessage] = React.useState('');
  const [analysisStatusErrorMessage, setAnalysisStatusErrorMessage] = React.useState('');
  const filterState = useSelector((state) => state.filter);
  const dateRange = React.useMemo(() => getApiDateRangeFromFilterState(filterState), [filterState]);
  const groupBy = React.useMemo(() => getApiGroupByFromFilterState(filterState), [filterState]);

  React.useEffect(() => {
    if (!canView) {
      setOverviewData(null);
      setIsLoadingOverview(false);
      setOverviewErrorMessage('Seu perfil não possui permissão para visualizar transações.');
      return undefined;
    }

    let isMounted = true;

    async function fetchOverview() {
      if (isMounted)
        setIsLoadingOverview(true);
      if (isMounted)
        setOverviewErrorMessage('');

      const rulesUrl = process.env.REACT_APP_API_RULES_URL;

      if (!rulesUrl) {
        if (isMounted)
          setOverviewData(null);
        if (isMounted)
          setIsLoadingOverview(false);
        return;
      }

      try {
        const payload = await rulesEngineGet(getRulesEngineAnalyticsUrl(rulesUrl, 'overview'), {
          params: {
            start_date: dateRange.startDate,
            end_date: dateRange.endDate,
          },
        });

        if (isMounted)
          setOverviewData(payload);
      } catch (error) {
        if (isMounted) {
          setOverviewData(null);
          setOverviewErrorMessage(getRulesEngineUiMessage(error));
        }
      } finally {
        if (isMounted)
          setIsLoadingOverview(false);
      }
    }

    fetchOverview();

    return () => {
      isMounted = false;
    };
  }, [canView, dateRange.endDate, dateRange.startDate]);

  React.useEffect(() => {
    if (!canView) {
      setAnalysisStatusHistory([]);
      setIsLoadingAnalysisStatusHistory(false);
      setAnalysisStatusErrorMessage('Seu perfil não possui permissão para visualizar transações.');
      return undefined;
    }

    let isMounted = true;

    async function fetchAnalysisStatusHistory() {
      if (isMounted)
        setIsLoadingAnalysisStatusHistory(true);
      if (isMounted)
        setAnalysisStatusErrorMessage('');

      const rulesUrl = process.env.REACT_APP_API_RULES_URL;

      if (!rulesUrl) {
        if (isMounted)
          setAnalysisStatusHistory([]);
        if (isMounted)
          setIsLoadingAnalysisStatusHistory(false);
        return;
      }

      try {
        const payload = await rulesEngineGet(getRulesEngineAnalyticsUrl(rulesUrl, 'analysis_status_distribution'), {
          params: {
            start_date: dateRange.startDate,
            end_date: dateRange.endDate,
            group_by: groupBy,
            only_alerts: 'true',
          },
        });

        if (isMounted)
          setAnalysisStatusHistory(Array.isArray(payload) ? payload : []);
      } catch (error) {
        if (isMounted) {
          setAnalysisStatusHistory([]);
          setAnalysisStatusErrorMessage(getRulesEngineUiMessage(error));
        }
      } finally {
        if (isMounted)
          setIsLoadingAnalysisStatusHistory(false);
      }
    }

    fetchAnalysisStatusHistory();

    return () => {
      isMounted = false;
    };
  }, [canView, dateRange.endDate, dateRange.startDate, groupBy]);

  const fallbackSeries = React.useMemo(
    () => Array.from({ length: config.length }, () => 0),
    [config.length],
  );

  const statusTotals = React.useMemo(() => {
    if (!analysisStatusHistory.length)
      return { count: {}, volume_raw: {} };

    const totalBucket = analysisStatusHistory.find((item) => item?._id === 'TOTAL');

    if (totalBucket?.analysis_status)
      return {
        count: totalBucket.analysis_status.count || {},
        volume_raw: totalBucket.analysis_status.volume_raw || {},
      };

    return analysisStatusHistory.reduce((accumulator, item) => {
      const counts = item?.analysis_status?.count || {};
      const volumes = item?.analysis_status?.volume_raw || {};

      Object.keys(counts).forEach((key) => {
        accumulator.count[key] = (accumulator.count[key] || 0) + (Number(counts[key]) || 0);
      });

      Object.keys(volumes).forEach((key) => {
        accumulator.volume_raw[key] = (accumulator.volume_raw[key] || 0) + (Number(volumes[key]) || 0);
      });

      return accumulator;
    }, { count: {}, volume_raw: {} });
  }, [analysisStatusHistory]);

  const normalizedAnalysisStatusHistory = React.useMemo(() => {
    if (!Array.isArray(analysisStatusHistory) || !analysisStatusHistory.length)
      return { items: [], labels: [] };

    const bucketKeys = generateBucketKeys(dateRange.startDate, dateRange.endDate, groupBy);
    const bucketKeySet = new Set(bucketKeys);
    const bucketMap = analysisStatusHistory.reduce((accumulator, item) => {
      if (!item || item._id === 'TOTAL')
        return accumulator;

      const key = normalizeBucketKey(item._id, groupBy);

      if (!bucketKeySet.has(key))
        return accumulator;

      accumulator.set(key, item);
      return accumulator;
    }, new Map());

    const items = bucketKeys.map((key) => bucketMap.get(key) || { _id: key, analysis_status: { count: {}, volume_raw: {} } });

    return {
      items,
      labels: bucketKeys.map((key) => formatHistoryLabel(key, groupBy)),
    };
  }, [analysisStatusHistory, dateRange.endDate, dateRange.startDate, groupBy]);

  const historyLabels = normalizedAnalysisStatusHistory.labels.length
    ? normalizedAnalysisStatusHistory.labels
    : config.labels;

  const totalCountSeries = React.useMemo(() => {
    if (!normalizedAnalysisStatusHistory.items.length)
      return [];

    return normalizedAnalysisStatusHistory.items.map((item) => {
      const counts = item?.analysis_status?.count || {};
      return Object.values(counts).reduce((sum, value) => sum + (Number(value) || 0), 0);
    });
  }, [normalizedAnalysisStatusHistory.items]);

  const totalVolumeSeries = React.useMemo(() => {
    if (!normalizedAnalysisStatusHistory.items.length)
      return [];

    return normalizedAnalysisStatusHistory.items.map((item) => {
      const volumes = item?.analysis_status?.volume_raw || {};
      return Object.values(volumes).reduce((sum, value) => sum + (Number(value) || 0), 0);
    });
  }, [normalizedAnalysisStatusHistory.items]);

  const financiesCards = React.useMemo(() => (
    AML_TRANSACTIONS_FINANCIAL_CARDS.map((card, index) => {
      const normalizedCard = {
        ...card,
        interval: intervalLabel,
        valueFormatter: formatBrlTooltipValue,
        statusTone: STATUS_TONE_BY_CARD_TITLE[card.title],
      };

      if (!overviewData)
        return {
          ...normalizedCard,
          labels: config.labels,
          data: fallbackSeries,
        };

      if (index === 0) {
        const overviewTotalVolumePercentage = overviewData.financial?.alert_volume_percentage;
        const overviewTotalVolumePercentageFormatted = overviewData.financial?.alert_volume_percentage_formatted;

        return {
          ...normalizedCard,
          value: overviewData.financial?.volume_with_alerts || normalizedCard.value,
          trend: overviewTotalVolumePercentage !== undefined && overviewTotalVolumePercentage !== null
            ? getTrendByPercentage(overviewTotalVolumePercentage)
            : normalizedCard.trend,
          variation: overviewTotalVolumePercentageFormatted || normalizedCard.variation,
          labels: historyLabels,
          data: totalVolumeSeries.length ? totalVolumeSeries : normalizedCard.data,
        };
      }

      const statusKey = ANALYSIS_STATUS_BY_TRANSACTION_CARD_TITLE[normalizedCard.title];

      if (!statusKey)
        return normalizedCard;

      const overviewStatusVolumeRaw = overviewData.analysis?.analysis_status?.volume_raw?.[statusKey];
      const overviewStatusVolumePercentage = overviewData.analysis?.analysis_status?.volume_percentage?.[statusKey];
      const overviewStatusVolumePercentageFormatted = overviewData.analysis?.analysis_status?.volume_percentage_formatted?.[statusKey];
      const overviewStatusCountPercentage = overviewData.analysis?.analysis_status?.count_percentage?.[statusKey];
      const overviewStatusCountPercentageFormatted = overviewData.analysis?.analysis_status?.count_percentage_formatted?.[statusKey];
      const statusPercentageValue = overviewStatusVolumePercentage ?? overviewStatusCountPercentage;
      const statusVariationLabel = overviewStatusVolumePercentageFormatted ?? overviewStatusCountPercentageFormatted;

      return {
        ...normalizedCard,
        value: formatBrl(
          overviewStatusVolumeRaw !== undefined && overviewStatusVolumeRaw !== null
            ? overviewStatusVolumeRaw
            : statusTotals.volume_raw?.[statusKey],
        ),
        trend: statusPercentageValue !== undefined && statusPercentageValue !== null
          ? getTrendByPercentage(statusPercentageValue)
          : normalizedCard.trend,
        variation: statusVariationLabel || normalizedCard.variation,
        labels: historyLabels,
        data: normalizedAnalysisStatusHistory.items.length
          ? normalizedAnalysisStatusHistory.items.map((item) => Number(item.analysis_status?.volume_raw?.[statusKey]) || 0)
          : normalizedCard.data,
      };
    })
  ), [config.labels, fallbackSeries, historyLabels, intervalLabel, normalizedAnalysisStatusHistory.items, overviewData, statusTotals.volume_raw, totalVolumeSeries]);

  const quantitiesCards = React.useMemo(() => (
    AML_TRANSACTIONS_QUANTITY_CARDS.map((card, index) => {
      const normalizedCard = {
        ...card,
        interval: intervalLabel,
        statusTone: STATUS_TONE_BY_CARD_TITLE[card.title],
      };

      if (!overviewData)
        return {
          ...normalizedCard,
          labels: config.labels,
          data: fallbackSeries,
        };

      if (index === 0) {
        const overviewTotalAlertRate = overviewData.transactions?.alert_rate_percentage ?? overviewData.alerts?.alert_rate_percentage;

        return {
          ...normalizedCard,
          value: formatCount(overviewData.transactions?.transactions_with_alerts),
          trend: overviewTotalAlertRate !== undefined && overviewTotalAlertRate !== null
            ? getTrendByPercentage(overviewTotalAlertRate)
            : normalizedCard.trend,
          variation: formatPercentage(overviewTotalAlertRate) || normalizedCard.variation,
          labels: historyLabels,
          data: totalCountSeries.length ? totalCountSeries : normalizedCard.data,
        };
      }

      const statusKey = ANALYSIS_STATUS_BY_TRANSACTION_CARD_TITLE[normalizedCard.title];

      if (!statusKey)
        return normalizedCard;

      const overviewStatusCount = overviewData.analysis?.analysis_status?.count?.[statusKey];
      const overviewStatusCountPercentage = overviewData.analysis?.analysis_status?.count_percentage?.[statusKey];
      const overviewStatusCountPercentageFormatted = overviewData.analysis?.analysis_status?.count_percentage_formatted?.[statusKey];

      return {
        ...normalizedCard,
        value: formatCount(
          overviewStatusCount !== undefined && overviewStatusCount !== null
            ? overviewStatusCount
            : statusTotals.count?.[statusKey],
        ),
        trend: overviewStatusCountPercentage !== undefined && overviewStatusCountPercentage !== null
          ? getTrendByPercentage(overviewStatusCountPercentage)
          : normalizedCard.trend,
        variation: overviewStatusCountPercentageFormatted || normalizedCard.variation,
        labels: historyLabels,
        data: normalizedAnalysisStatusHistory.items.length
          ? normalizedAnalysisStatusHistory.items.map((item) => Number(item.analysis_status?.count?.[statusKey]) || 0)
          : normalizedCard.data,
      };
    })
  ), [config.labels, fallbackSeries, historyLabels, intervalLabel, normalizedAnalysisStatusHistory.items, overviewData, statusTotals.count, totalCountSeries]);

  const isLoadingData = isLoadingOverview || isLoadingAnalysisStatusHistory;
  const dataErrorMessage = overviewErrorMessage || analysisStatusErrorMessage;

  const handleAnalysisStatusChange = React.useCallback((_, nextStatus) => {
    const normalizedStatus = normalizeAnalysisStatus(nextStatus);
    const nextSearchParams = new URLSearchParams(searchParams);

    if (normalizedStatus === 'ALL') {
      nextSearchParams.delete('status');
    } else {
      nextSearchParams.set('status', normalizedStatus);
    }

    setSearchParams(nextSearchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  if (!canView) {
    return <Alert severity="warning">Seu perfil não possui permissão para visualizar transações.</Alert>;
  }

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
      {/* cards */}
      <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
        {AML_TRANSACTIONS_GRID_TEXT.title}
      </Typography>
      {dataErrorMessage ? (
        <Box sx={{ mb: 2, p: 1.5, borderRadius: 1, bgcolor: 'error.light' }}>
          <Typography variant="body2" sx={{ color: 'error.contrastText' }}>
            {dataErrorMessage}
          </Typography>
        </Box>
      ) : null}
      {isLoadingData ? (
        <Box sx={{ minHeight: 320, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 2 }}>
          <CircularProgress size={28} />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {AML_TRANSACTIONS_GRID_TEXT.loading}
          </Typography>
        </Box>
      ) : (
        <>
          <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
            {AML_TRANSACTIONS_GRID_TEXT.financialSection}
          </Typography>
          <Grid
            container
            spacing={2}
            columns={12}
            sx={{ mb: (theme) => theme.spacing(2) }}
          >
            {financiesCards.map((card, index) => (
              <Grid key={index} size={{ xs: 12, sm: 6, md: 6, lg: 4, xl: 2.4 }}>
                <StatCard {...card} valueSize="financial" />
              </Grid>
            ))}
          </Grid>
          <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
            {AML_TRANSACTIONS_GRID_TEXT.quantitativeSection}
          </Typography>
          <Grid
            container
            spacing={2}
            columns={12}
            sx={{ mb: (theme) => theme.spacing(2) }}
          >
            {quantitiesCards.map((card, index) => (
              <Grid key={index} size={{ xs: 12, sm: 6, md: 6, lg: 4, xl: 2.4 }}>
                <StatCard {...card} valueSize="compact" />
              </Grid>
            ))}
          </Grid>
        </>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography component="h2" variant="h6">
          {AML_TRANSACTIONS_GRID_TEXT.detailsSection}
        </Typography>
      </Box>
      <Tabs
        value={analysisStatus}
        onChange={handleAnalysisStatusChange}
        aria-label="Filtros por status"
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ mb: 2 }}
      >
        {ANALYSIS_STATUS_TABS.map((tab) => (
          <Tab
            key={tab.value}
            label={tab.label}
            value={tab.value}
          />
        ))}
      </Tabs>
      <Grid container spacing={2} columns={12}>
        <CustomizedDataGrid analysisStatus={analysisStatus} />
      </Grid>
      <Copyright sx={{ my: 4 }} />
    </Box>
  );
}
